import { useEffect, useState, useMemo, useRef } from "react";
import WidgetContainer from "../reusablesComponents/containerWidget";
import { getPositionsTable } from "../../services/getPositionsTable";
import NoDataMessage from "../positionsTable/noDataMessage/noDataMessage";
import TableBaseball from "./tableBaseball/tableBaseball";
import TableBasquetball from "./tableBasquetball/tableBasquetball";
import TableFutbol from "./tableFutbol/tableFutbol";
import TableMotor from "./tableMotor/tableMotor";
import TableTenis from "./tableTenis/tableTenis";
import TableNFL from "./tableNFL/tableNFL";
import TableGolf from "./tableGolf/tableGolf";
import "./positionsTable.sass";


/**
 * Configuración de deportes con sus componentes y validaciones
 */
const SPORT_CONFIG = {
  soccer: { component: TableFutbol, dataKey: 'soccerStages', title: 'Posiciones' },
  baseball: { component: TableBaseball, dataKey: 'baseballStages', title: 'Posiciones' },
  basketball: { component: TableBasquetball, dataKey: 'baskStages', title: 'Posiciones' },
  motorsport: { component: TableMotor, dataKey: 'motorsportStanding', title: 'Clasificaciones' },
  football: { component: TableNFL, dataKey: 'football', title: 'Posiciones' },
  tenis: { component: TableTenis, dataKey: 'tennisStages', title: 'Ranking de Jugadores', isSpecial: true },
  golf: { component: TableGolf, dataKey: 'golfStages', title: 'Ranking de Jugadores', isSpecial: true }
};

/**
 * Componente principal para tablas de posiciones deportivas
 * Maneja la carga de datos y renderizado dinámico según el deporte
 */
export default function PositionsTable({ sport, idEvent, version }) {
  const [dataTab1, setDataTab1] = useState({});
  const [loading, setLoading] = useState(true);
  const fetchingRef = useRef(false);

  // Configuración del deporte actual
  const sportConfig = useMemo(() => SPORT_CONFIG[sport] || null, [sport]);

  /**
   * Carga los datos de la tabla de posiciones
   */
  useEffect(() => {
    if (fetchingRef.current) return;
    
    const fetchData = async () => {
      fetchingRef.current = true;
      setLoading(true);
      try {
        const result = await getPositionsTable({ sport, idEvent });
        setDataTab1(result.statusCode === 200 ? result.data : {});
      } catch (error) {
        console.error("Error al obtener datos de tabla:", error);
        setDataTab1({});
      } finally {
        setLoading(false);
        fetchingRef.current = false;
      }
    };

    fetchData();
  }, [sport, idEvent]);

  /**
   * Valida si hay datos disponibles para el deporte
   */
  const hasValidData = useMemo(() => {
    if (!dataTab1 || !sportConfig) return false;

    const { dataKey, isSpecial } = sportConfig;
    const data = dataTab1[dataKey];

    if (isSpecial) {
      // Tenis: tennisStages.ranking debe ser array con elementos
      return data?.ranking && Array.isArray(data.ranking) && data.ranking.length > 0;
    }

    if (sport === 'motorsport') {
      // Motorsport: motorsportStanding debe ser objeto con datos
      return data && typeof data === 'object' && Object.keys(data).length > 0;
    }

    if (sport === 'baseball') {
      // Baseball: validar que haya stages con conferencias y divisiones válidas
      return Array.isArray(data) && data.length > 0 && 
        data.some(stage => 
          stage.conferences && Array.isArray(stage.conferences) &&
          stage.conferences.some(conference => 
            conference.divisions && Array.isArray(conference.divisions) && 
            conference.divisions.length > 0
          )
        );
    }

    // Otros deportes: dataKey debe ser array con elementos
    return Array.isArray(data) && data.length > 0;
  }, [dataTab1, sportConfig, sport]);

  /**
   * Renderiza el contenido principal
   */
  const renderContent = () => {
    if (!sportConfig) {
      return <NoDataMessage title="Deporte no soportado" />;
    }

    if (!hasValidData) {
      return <NoDataMessage title={sportConfig.title} />;
    }

    const TableComponent = sportConfig.component;
    return <TableComponent dataTab1={dataTab1} sport={sport} version={version}/>;
  };

  return (
    <WidgetContainer 
      loading={loading} 
      heightLoading="" 
      className="container__positions-table" 
      id="container__positions-table"
    >
      <div className="positions-table__component">
        {renderContent()}
      </div>
    </WidgetContainer>
  );
}